import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Box, Button, Typography } from "@mui/material";
import AxiosInstance from "../../AxiosInstance.jsx";
import { useFormik } from "formik";
import * as Yup from "yup";
import { postFile } from "../../../components/Files/Functions.jsx";
import InvoiceViews from "./Views/Invoice.jsx";
import sendSwal from "../../../components/Swal/sendSwal.jsx";
import { DeleteIcone, EditIcon } from "../../../components/Icon/Index.jsx";
import showToast from "../../../components/Toast/Toster.jsx";
import { handleAuth } from "../../../components/Login/Auth.jsx";
import { useStaffContext } from "../../../components/StaffData/Staffdata.jsx";
import { useUserType } from "../../../contexts/UserTypeContext.jsx";
import CustomTooltip from "../../../components/Tooltip/CustomTooltip.jsx";
import dayjs from "dayjs";
import { useUserTimezoneFormatter } from "../../../components/UserTimezoneDateFormatter/UserTimezoneDateFormatter";
import {
  JobberAvatar,
  JobberStatusBadge,
  JobberTableDocRefTitleCell,
  getJobberStatusTone,
} from "../../../components/MuiTable/index.jsx";

const INVOICE_EDIT_TILE_BG = "#DCFCE7";
const INVOICE_EDIT_TILE_BORDER = "rgba(4, 120, 87, 0.18)";
const INVOICE_EDIT_ICON = "#047857";
const INVOICE_DELETE_TILE_BG = "#FEE2E2";
const INVOICE_DELETE_TILE_BORDER = "rgba(185, 28, 28, 0.15)";
const INVOICE_DELETE_ICON = "#B91C1C";
const INVOICE_ACTION_DISABLED_BG = "#F3F4F6";
const INVOICE_ACTION_DISABLED_BORDER = "rgba(0, 0, 0, 0.08)";
const INVOICE_ACTION_DISABLED_ICON = "#9CA3AF";

const invoiceMobileRowBtnSx = {
  width: "100%",
  textTransform: "none",
  fontWeight: 600,
  fontSize: "0.7rem",
  borderRadius: "10px",
  minHeight: 36,
  px: 0.5,
  py: 0.75,
  lineHeight: 1.2,
  boxShadow: "none",
  whiteSpace: "nowrap",
  wordBreak: "normal",
  overflow: "hidden",
  textOverflow: "ellipsis",
  textAlign: "center",
};

const Invoice = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { CompanyUrl } = useParams();
  const baseUrl = import.meta.env.VITE_BASE_API;
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loader, setLoader] = useState(true);
  const [isCustomer, setIsCustomer] = useState(false);
  const [source, setModalSource] = useState(false);
  const [countData, setCountData] = useState(null);
  const [isProperty, setIsProperty] = useState(false);
  const [propertyData, setPropertyData] = useState({});
  const [customersData, setCustomersData] = useState({});
  const [loading, setLoading] = useState(false);
  const { staffData } = useStaffContext();
  const { userType } = useUserType();
  const isDonationMode = userType === "donor";

  const [invoiceData, setInvoiceData] = useState([]);
  const [tokenDecode, setTokenDecode] = useState({});
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [lineItems, setLineItems] = useState([
    {
      Description: "",
      Name: "",
      Type: "",
      Units: "",
      Attachment: "",
      CostPerUnit: "",
      Cost: "",
      Markup: "",
      Total: "",
      isNew: true,
    },
  ]);

  const [DateDecode, setDateDecode] = useState("MM/DD/YYYY");
 
  const [dateFormat, setDateFormat] = useState("MM/DD/YYYY");
  const [nameFormat, setNameFormat] = useState("FirstNameToLastName");
  // JOB-3170: default sort by createdAt so newly created invoices always
  // land on page 1. The prior `updatedAt` default scattered new invoices
  // into the middle of the list when older rows had recent edits.
  const [sortField, setSortField] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");

  // Use timezone-aware date formatter hook
  const formatDateWithUserTimezone = useUserTimezoneFormatter();

  useEffect(() => {
    (async () => {
      try {
        const res = await handleAuth(navigate, location);
        setTokenDecode(res?.data ?? {});
        setDateDecode(res?.themes);
        setNameFormat(res?.data?.NameSortOrder || "FirstNameToLastName");
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoader(false);
      }
    })();
  }, [navigate, location]);

  const handleSearchChange = useCallback((value) => {
    setSearch(value ?? "");
    setPage(0);
  }, []);

  const getData = useCallback(
    async (CompanyId) => {
      if (!CompanyId) return;
      setLoader(true);
      try {
        const res = await AxiosInstance.get(
          `/v1/invoice/invoice_table/${CompanyId}`,
          {
            params: {
              pageSize: rowsPerPage,
              pageNumber: page,
              search: search?.trim() ? search.trim() : "",
              statusFilter:
                selectedStatus == null || selectedStatus === ""
                  ? undefined
                  : selectedStatus,
              sortField,
              sortOrder,
            },
          }
        );
        if (res?.data?.statusCode === 200) {
          setInvoiceData(res?.data?.data || []);
          setCountData(res?.data?.totalCount ?? 0);
        } else {
          setInvoiceData([]);
          setCountData(0);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        setInvoiceData([]);
        setCountData(0);
      } finally {
        setLoader(false);
      }
    },
    [page, rowsPerPage, search, selectedStatus, sortField, sortOrder]
  );

  useEffect(() => {
    const companyId = tokenDecode?.CompanyId;
    if (!companyId) return;
    getData(companyId);
  }, [tokenDecode?.CompanyId, getData]);

  useEffect(() => {
    if (DateDecode?.Format) {
      setDateFormat(DateDecode.Format);
    } else {
      setDateFormat("MM/DD/YYYY");
    }
  }, [DateDecode]);

  // Open customer modal when navigating from dashboard with openCustomerModal flag
  useEffect(() => {
    if (location?.state?.openCustomerModal === true) {
      setIsCustomer(true);
      setModalSource("Invoice");
    }
  }, [location?.state?.openCustomerModal]);

  const handleEditClick = (id) => {
    if (tokenDecode?.Role === "Worker") {
      navigate(`/${CompanyUrl}/staff-member/addinvoice`, {
        state: {
          invoiceId: id,
          navigats: [
            "/index",
            "/staff-member",
            "/worker-invoice",
            "/addinvoice",
          ],
        },
      });
    } else if (CompanyUrl) {
      navigate(`/${CompanyUrl}/addinvoice`, {
        state: {
          invoiceId: id,
          navigats: ["/index", "/invoice", "/addinvoice"],
        },
      });
    }
  };

  const handleDelete = (id) => {
    sendSwal(isDonationMode).then(async (deleteReason) => {
      if (deleteReason) {
        try {
          const invoiceToDelete = invoiceData?.find(
            (invoice) => invoice?.InvoiceId === id
          );

          if (!invoiceToDelete) {
            showToast.error("Invoice data not found");
            return;
          }

          const response = await AxiosInstance.delete(`/v1/invoice/${id}`, {
            data: { DeleteReason: deleteReason },
          });
          if (response?.data?.statusCode === 200) {
            const toastId = showToast.success(
              response?.data?.message || "Invoice deleted successfully.",
              {
                position: "top-center",
                autoClose: 2000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: false,
                draggable: false,
                closeButton: false,
              }
            );

            setInvoiceData((prevData) =>
              prevData?.filter((item) => item?.InvoiceId !== id)
            );

            if (tokenDecode?.CompanyId) {
              getData(tokenDecode.CompanyId);
            }
            window.dispatchEvent(new Event("sidebar-counts-refresh"));
          } else {
            showToast.warning("", response?.data?.message, "error");
          }
        } catch (error) {
          console.error("Error:", error);
          showToast.error(error);
        }
      }
    });
    setTimeout(() => {
      const deleteButton = document.querySelector(".swal-button--confirm");
      if (deleteButton) {
        deleteButton.disabled = true;
      }
    }, 0);
  };

  const formik = useFormik({
    initialValues: {
      Title: "",
      InvoiceNumber: 1,
      CustomerId: "",
      CompanyId: tokenDecode?.CompanyId,
      LocationId: "",
      CustomerMessage: "",
      ContractDisclaimer:
        "Contract / Disclaimer\nThis quote is valid for the next 30 days, after which values may be subject to change.",
      Attachment: [],
      Discount: "",
    },
    validationSchema: Yup.object({
      Title: Yup.string().required("Title is Required"),
    }),
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const strings = [];
        for (let item of values?.Attachment || []) {
          if (typeof item !== "string") {
            const string = await postFile(item);
            strings.push(string);
          } else {
            strings.push(item);
          }
        }

        for (let item of lineItems || []) {
          if (typeof item?.Attachment !== "string") {
            const string = await postFile(item?.Attachment);
            item.Attachment = string;
          }
        }
        const object = {
          ...values,
          details: lineItems,
          Attachment: strings,
        };
        let response;
        if (!location?.state?.id) {
          response = await AxiosInstance.post(`/v1/invoice`, object);
        } else {
          response = await AxiosInstance.put(
            `/v1/invoice/${location?.state?.id}`,
            object
          );
        }

        if (response.status === 200) {
          showToast.success(response?.data?.message || response?.message);
          navigate(`/${CompanyUrl}/invoice`, {
            state: {
              navigats: location?.state?.navigats?.filter(
                (item) => item !== "/AddInvoice"
              ),
            },
          });
        }
      } catch (error) {
        showToast.error("", error?.message, "error");
        console.error("Error: ", error);
      } finally {
        setLoading(false);
      }
    },
  });
 
  /** Same destination/state as JobberTable row navigation on invoices list */
  const goToInvoiceDetail = (e, invoiceId) => {
    if (e) e.stopPropagation();
    if (!invoiceId) return;
    const navigatePath =
      tokenDecode?.Role === "Worker"
        ? `/${CompanyUrl}/staff-member/worker-invoice-detail`
        : `/${CompanyUrl}/invoice-details`;
    const newPath = navigatePath.split("/");
    const baseNavigats = location?.pathname?.includes("staff-member")
      ? ["/index", "/staff-member", "/worker-invoice"]
      : ["/index", "/invoice"];
    navigate(navigatePath, {
      state: {
        id: invoiceId,
        navigats: [...baseNavigats, `/${newPath[newPath.length - 1]}`],
      },
    });
  };

  const cellData = invoiceData?.map((item, index) => {
    // Helper function to format address
    const formatAddress = (location) => {
      if (!location) return "Address not available";
      
      const address = location.Address || "";
      const city = location.City || "";
      const state = location.State || "";
      const zip = location.ZipCode || location.Zip || "";
      
      // Check if all address fields are empty
      if (!address && !city && !state && !zip) {
        return "Address not available";
      }
      
      // Build address string with only available fields
      const addressParts = [];
      if (address) addressParts.push(address);
      if (city) addressParts.push(city);
      if (state) addressParts.push(state);
      if (zip) addressParts.push(zip);
      
      return addressParts.join(", ");
    };
    
    const addressString = formatAddress(item?.location);

    const firstName = item?.customer?.FirstName || "N/A";
    const lastName = item?.customer?.LastName || "N/A";
    const customerFullName =
      nameFormat === "LastNameToFirstName"
        ? `${lastName}, ${firstName}`.trim()
        : `${firstName} ${lastName}`.trim();

    const emailStr =
      item?.customer?.EmailAddress || "Email not available";

    const editLocked =
      item?.Status === "Canceled" ||
      item?.Status === "Void" ||
      item?.Status === "Paid";
    const deleteLocked = Boolean(staffData?.Invoice?.ViewOnly);

    const invoiceLinkColor = isDonationMode ? "#29B3E2" : "#2563EB";

    return {
      key: item?.InvoiceId || index,
      value: [
        <div
          key={`inv-name-${item?.InvoiceId ?? index}`}
          className="customer-table-name-block"
        >
          <JobberAvatar
            name={customerFullName}
            colorSeed={item?.InvoiceId ?? `row-${index}`}
            showName={false}
          />
          <div className="customer-table-name-block__text">
            <span className="jobber-avatar-inline__name customer-table-name-block__name">
              {customerFullName}
            </span>
            <div className="customer-table-name-block__email">{emailStr}</div>
          </div>
        </div>,
        <JobberTableDocRefTitleCell
          key={`inv-num-${item?.InvoiceId ?? index}`}
          refNumber={item?.InvoiceNumber}
          onRefClick={(e) => goToInvoiceDetail(e, item?.InvoiceId)}
          linkColor={invoiceLinkColor}
          title={item?.Subject || item?.account_name}
          emptyTitleLabel="N/A"
          titleLineBreakStyle="multi-line"
        />,
            <div
              style={{
                whiteSpace: "normal",
                wordBreak: "break-word",
                width: "250px",
                maxWidth: "100%",
                lineHeight: "1.5",
              }}
            >
              {addressString}
            </div>,
        formatDateWithUserTimezone(item?.createdAt, {
          dateFormat: dateFormat,
          includeSeconds: false
        }),
        item?.DueDate
          ? formatDateWithUserTimezone(item?.DueDate, {
              dateFormat: dateFormat,
              includeSeconds: false,
              excludeTime: true,
            })
          : "—",

        <Box
          key={`inv-total-${item?.InvoiceId ?? index}`}
          sx={{ textAlign: "right", width: "100%" }}
        >
          <Typography
            component="span"
            sx={{
              color: "text.primary",
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
              fontSize: "0.9375rem",
            }}
          >
            {`$${new Intl.NumberFormat("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(item?.Total ?? 0)}`}
          </Typography>
        </Box>,
        <Box
          key={`inv-due-${item?.InvoiceId ?? index}`}
          sx={{ textAlign: "right", width: "100%" }}
        >
          <Typography
            component="span"
            sx={{
              color: "text.primary",
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
              fontSize: "0.9375rem",
            }}
          >
            {`$${new Intl.NumberFormat("en-US", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(Math.abs(item?.invoiceAccount ?? 0))}`}
          </Typography>
        </Box>,
        <JobberStatusBadge
          key={`st-${item?.InvoiceId ?? index}`}
          tone={getJobberStatusTone(item?.Status)}
        >
          {item?.Status || "N/A"}
        </JobberStatusBadge>,
        <>
          {(staffData?.Invoice?.ViewCreateAndEdit ||
            staffData?.Invoice?.ViewCreateEditAndDelete ||
            !staffData) && (
            <>
              <Box
                className="customer-list-actions-desktop"
                sx={{ gap: "8px", alignItems: "center" }}
              >
                <Box
                  component="span"
                  onClick={
                    editLocked
                      ? undefined
                      : (e) => {
                          e.stopPropagation();
                          handleEditClick(item?.InvoiceId);
                        }
                  }
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 36,
                    height: 36,
                    borderRadius: "8px",
                    backgroundColor: editLocked
                      ? INVOICE_ACTION_DISABLED_BG
                      : INVOICE_EDIT_TILE_BG,
                    border: `1px solid ${
                      editLocked
                        ? INVOICE_ACTION_DISABLED_BORDER
                        : INVOICE_EDIT_TILE_BORDER
                    }`,
                    verticalAlign: "middle",
                    lineHeight: 0,
                    opacity: editLocked ? 0.65 : 1,
                    cursor: editLocked ? "not-allowed" : "pointer",
                  }}
                >
                  <EditIcon
                    color={
                      editLocked
                        ? INVOICE_ACTION_DISABLED_ICON
                        : INVOICE_EDIT_ICON
                    }
                    style={{
                      cursor: editLocked ? "not-allowed" : "pointer",
                      marginRight: 0,
                    }}
                  />
                </Box>
                {(staffData?.Invoice?.ViewCreateEditAndDelete || !staffData) && (
                  <Box
                    component="span"
                    onClick={
                      deleteLocked
                        ? undefined
                        : (e) => {
                            e.stopPropagation();
                            handleDelete(item?.InvoiceId);
                          }
                    }
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 36,
                      height: 36,
                      borderRadius: "8px",
                      backgroundColor: deleteLocked
                        ? INVOICE_ACTION_DISABLED_BG
                        : INVOICE_DELETE_TILE_BG,
                      border: `1px solid ${
                        deleteLocked
                          ? INVOICE_ACTION_DISABLED_BORDER
                          : INVOICE_DELETE_TILE_BORDER
                      }`,
                      verticalAlign: "middle",
                      lineHeight: 0,
                      opacity: deleteLocked ? 0.65 : 1,
                      cursor: deleteLocked ? "not-allowed" : "pointer",
                    }}
                  >
                    <DeleteIcone
                      className="customerEditImgToEdit"
                      color={
                        deleteLocked
                          ? INVOICE_ACTION_DISABLED_ICON
                          : INVOICE_DELETE_ICON
                      }
                      style={{
                        cursor: deleteLocked ? "not-allowed" : "pointer",
                        marginRight: 0,
                      }}
                      disabled={staffData?.Invoice?.ViewOnly}
                    />
                  </Box>
                )}
              </Box>

              <Box className="customer-list-actions-mobile">
                <Button
                  sx={{
                    ...invoiceMobileRowBtnSx,
                    backgroundColor: "#DBEAFE",
                    border: "1px solid #93C5FD",
                    color: "#1D4ED8",
                    "&:hover": {
                      backgroundColor: "#BFDBFE",
                      borderColor: "#93C5FD",
                    },
                  }}
                  onClick={(e) => goToInvoiceDetail(e, item?.InvoiceId)}
                >
                  View
                </Button>
                <Button
                  sx={{
                    ...invoiceMobileRowBtnSx,
                    backgroundColor: "#DBEAFE",
                    border: "1px solid #93C5FD",
                    color: "#1D4ED8",
                    "&:hover": {
                      backgroundColor: "#BFDBFE",
                      borderColor: "#93C5FD",
                    },
                  }}
                  disabled={editLocked}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!editLocked) handleEditClick(item?.InvoiceId);
                  }}
                >
                  Edit
                </Button>
                {(staffData?.Invoice?.ViewCreateEditAndDelete ||
                  !staffData) && (
                  <Button
                    sx={{
                      ...invoiceMobileRowBtnSx,
                      backgroundColor: "#FEE2E2",
                      border: "1px solid rgba(185, 28, 28, 0.15)",
                      color: "#B91C1C",
                      "&:hover": {
                        backgroundColor: "#FECACA",
                        borderColor: "rgba(185, 28, 28, 0.25)",
                      },
                    }}
                    disabled={deleteLocked}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (!deleteLocked) handleDelete(item?.InvoiceId);
                    }}
                  >
                    Delete
                  </Button>
                )}
              </Box>
            </>
          )}
        </>,
      ],
    };
  });

  const handleDropdownSelect = useCallback((status) => {
    setSelectedStatus(status === "All" ? null : status);
    setPage(0);
  }, []);

  const dropdownOptions = [
    { text: "All" },
    { text: "Draft" },
    { text: "Sent" },
    { text: "Past Due" },
    { text: "Partial" },
    { text: "Paid" },
    { text: "Void" },
    { text: "Unpaid" },
  ];

  const customerProperties =
    customersData && Object.keys(customersData).length > 0
      ? Array.isArray(customersData?.location)
        ? customersData?.location
        : Array.isArray(customersData?.locationDetails)
        ? customersData?.locationDetails
        : []
      : [];

  const handleCustomerClick = () => {
    if (customersData && Object.keys(customersData).length > 0) {
      const propertyCount = customerProperties.length;
      if (propertyCount === 1) {
        setPropertyData(customerProperties[0]);
        setIsProperty(false);
        setIsCustomer(false);
      } else {
        setIsProperty(true);
        setIsCustomer(false);
      }
    } else {
      setIsCustomer(true);
      setIsProperty(false);
    }
  };

  return (
    <>
      <InvoiceViews
        loader={loader}
        search={search}
        setSearch={handleSearchChange}
        cellData={cellData}
        setIsCustomer={setIsCustomer}
        setModalSource={setModalSource}
        page={page}
        setPage={setPage}
        setRowsPerPage={setRowsPerPage}
        CompanyUrl={CompanyUrl}
        countData={countData}
        rowsPerPage={rowsPerPage}
        isCustomer={isCustomer}
        formik={formik}
        lineItems={lineItems}
        propertyData={propertyData}
        setPropertyData={setPropertyData}
        isProperty={isProperty}
        setIsProperty={setIsProperty}
        customersData={customersData}
        setCustomersData={setCustomersData}
        source={source}
        staffData={staffData}
        dropdownOptions={dropdownOptions}
        handleDropdownSelect={handleDropdownSelect}
        setSortField={setSortField}
        setSortOrder={setSortOrder}
        sortOrder={sortOrder}
        sortField={sortField}
        tokenDecode={tokenDecode}
        handleCustomerClick={handleCustomerClick}
        customerProperties={customerProperties}
        isDonationMode={isDonationMode}
      />
    </>
  );
};

export default Invoice;
